import React, { useState, useRef } from 'react';

const UploadLecture = () => {
    const [lectureType, setLectureType] = useState('');
    const [date, setDate] = useState('');
    const [title, setTitle] = useState('');
    const [files, setFiles] = useState([]);
    const [fileError, setFileError] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const fileInputRef = useRef(null);

    const CHUNK_SIZE = 5 * 1024 * 1024;

    const generateId = () => Math.random().toString(36).substring(2, 10);

    const getProgressColor = (status) => {
        switch (status) {
            case 'uploading':
                return '#1DB954';
            case 'finalizing':
                return '#FBBC05';
            case 'complete':
                return '#1DB954';
            default:
                return '#1DB954';
        }
    };

    const clearForm = () => {
        setLectureType('');
        setDate('');
        setTitle('');
        setFiles([]);
        setUploadProgress({});
        setFileError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const duplicateFiles = [];

        const newFiles = selectedFiles
            .filter(newFile => {
                const isDuplicate = files.some(
                    existing =>
                        existing.file.name === newFile.name &&
                        existing.file.size === newFile.size
                );
                if (isDuplicate) {
                    duplicateFiles.push(newFile.name);
                }
                return !isDuplicate;
            })
            .map(file => ({
                file,
                id: generateId()
            }));

        if (duplicateFiles.length > 0) {
            setFileError(`"${duplicateFiles.join('", "')}" ${duplicateFiles.length > 1 ? 'are' : 'is'} already in your upload list.`);
        } else {
            setFileError('');
        }

        setFiles(prevFiles => [...prevFiles, ...newFiles]);
        setUploadStatus('');
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== indexToRemove));
    };

    const getLectureLabel = (index) => {
        const labels = ['First Lecture', 'Second Lecture', 'Third Lecture', 'Fourth Lecture', 'Fifth Lecture'];
        return labels[index] || `Lecture ${index + 1}`;
    };

    const createChunks = (file) => {
        const chunks = [];
        const chunksCount = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < chunksCount; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(file.size, start + CHUNK_SIZE);
            chunks.push(file.slice(start, end));
        }
        return chunks;
    };

    const uploadChunk = async (chunk, fileName, chunkIndex, totalChunks, fileId) => {
        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('fileName', fileName);
        formData.append('chunkIndex', chunkIndex);
        formData.append('totalChunks', totalChunks);
        formData.append('fileId', fileId);
        formData.append('fileExt', fileName.split('.').pop());

        try {
            const response = await fetch('/api/upload-chunk', {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                throw new Error(`Failed to upload chunk ${chunkIndex}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`Error uploading chunk ${chunkIndex}:`, error);
            throw error;
        }
    };

    const uploadFileInChunks = async (file, fileId) => {
        const chunks = createChunks(file);
        const totalChunks = chunks.length;

        setUploadProgress(prev => ({
            ...prev,
            [fileId]: { completed: 0, total: totalChunks, status: 'uploading' }
        }));

        for (let i = 0; i < totalChunks; i++) {
            await uploadChunk(chunks[i], file.name, i, totalChunks, fileId);
            setUploadProgress(prev => ({
                ...prev,
                [fileId]: { ...prev[fileId], completed: i + 1 }
            }));
        }

        setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], status: 'finalizing' }
        }));

        const finalizeResponse = await fetch('/api/finalize-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName: file.name, fileId, totalChunks })
        });

        if (!finalizeResponse.ok) {
            throw new Error('Failed to finalize upload');
        }

        setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], status: 'complete' }
        }));

        return await finalizeResponse.json();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (files.length === 0) {
            setFileError('Please upload at least one lecture audio file.');
            return;
        }

        setIsUploading(true);
        setUploadStatus('Uploading audio files...');

        try {
            const uploadResults = [];
            for (const item of files) {
                setUploadStatus(`Uploading ${item.file.name} in chunks...`);
                const result = await uploadFileInChunks(item.file, item.id);
                uploadResults.push(result);
            }

            setUploadStatus('Creating lecture entry...');

            const googleDriveLinks = uploadResults.map(result =>
                `http://docs.google.com/uc?export=open&id=${result.fileId}`
            );

            const lectureResponse = await fetch('/api/createLecture', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    date,
                    name: title,
                    type: lectureType,
                    lectures: googleDriveLinks
                }),
            });

            console.log({ date, name: title, type: lectureType, lectures: googleDriveLinks });

            const lectureResult = await lectureResponse.json();

            if (!lectureResponse.ok) {
                throw new Error(lectureResult.error || 'Lecture creation failed');
            }

            setUploadStatus('Upload and lecture creation successful!');

            if (lectureType === 'Tafseer ul Quran class') {
                document.querySelector('.tafseer').click();
            } else if (lectureType === 'Sahi Bukhari Class') {
                document.querySelector('.sahi').click();
            }
            clearForm();

        } catch (error) {
            console.error('Upload Error:', error);
            setUploadStatus(`Error: ${error.message}`);
            clearForm();
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="al-upload-form">
            <h2 className="al-upload-heading">Upload Lecture</h2>

            <div style={{ marginBottom: '20px' }}>
                <label className="al-upload-label">Lecture Type</label>
                <select
                    className="al-upload-input"
                    value={lectureType}
                    onChange={(e) => setLectureType(e.target.value)}
                    disabled={isUploading}
                    required
                >
                    <option value="">Select Type</option>
                    <option value="Tafseer ul Quran class">Tafseer ul Quran class</option>
                    <option value="Sahi Bukhari Class">Sahi Bukhari Class</option>
                </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label className="al-upload-label">Date</label>
                <input
                    className="al-upload-input"
                    type="text"
                    placeholder="e.g., January 9th 2020"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    disabled={isUploading}
                    required
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label className="al-upload-label">Lecture Title</label>
                <input
                    className="al-upload-input"
                    type="text"
                    placeholder="Enter lecture title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isUploading}
                    required
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label className="al-upload-label">Audio Files</label>
                <input
                    id="real-file-input"
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,video/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    disabled={isUploading}
                    multiple
                />
                <button
                    type="button"
                    className="al-upload-choose-btn"
                    onClick={() => document.getElementById('real-file-input').click()}
                    disabled={isUploading}
                >
                    Choose Files
                </button>

                {fileError && <p style={{ color: 'var(--delete-color)', marginTop: '8px', fontSize: '0.82rem' }}>{fileError}</p>}
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '15px', width: '100%' }}>
                    {files.map((item, index) => (
                        <li key={index} className="al-upload-file-item">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '8px', gap: '8px' }}>
                                <span className="al-upload-file-label">{getLectureLabel(index)}</span>
                                <span className="al-upload-file-name">{item.file.name}</span>
                                <span className="al-upload-file-size">
                                    {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                                    ({Math.ceil(item.file.size / CHUNK_SIZE)} chunks)
                                </span>
                                <button
                                    type="button"
                                    style={{
                                        backgroundColor: 'transparent',
                                        color: 'var(--delete-color)',
                                        border: '1px solid rgba(241,94,108,0.3)',
                                        borderRadius: '50%',
                                        width: '26px',
                                        height: '26px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: 0,
                                        flexShrink: 0,
                                    }}
                                    onClick={() => handleRemoveFile(index)}
                                    disabled={isUploading}
                                >
                                    x
                                </button>
                            </div>

                            {uploadProgress[item.id] && (
                                <div className="al-upload-progress-bg">
                                    <div
                                        style={{
                                            height: '100%',
                                            transition: 'width 0.3s ease, background-color 0.3s ease',
                                            borderRadius: '3px',
                                            width: `${Math.round((uploadProgress[item.id].completed / uploadProgress[item.id].total) * 100)}%`,
                                            backgroundColor: getProgressColor(uploadProgress[item.id].status)
                                        }}
                                    />
                                    <div className="al-upload-progress-text">
                                        {uploadProgress[item.id].status === 'finalizing'
                                            ? 'Finalizing...'
                                            : uploadProgress[item.id].status === 'complete'
                                                ? 'Complete'
                                                : `${Math.round((uploadProgress[item.id].completed / uploadProgress[item.id].total) * 100)}%`
                                        }
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <button type="submit" className="al-upload-submit-btn" disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Submit Lecture'}
            </button>

            {uploadStatus && (
                <p style={{
                    marginTop: '16px',
                    textAlign: 'center',
                    fontSize: '0.88rem',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontWeight: '500',
                    backgroundColor: uploadStatus.startsWith('Upload and') ? 'rgba(29,185,84,0.15)' : 'rgba(241,94,108,0.15)',
                    color: uploadStatus.startsWith('Upload and') ? '#1DB954' : '#f15e6c'
                }}>
                    {uploadStatus}
                </p>
            )}
        </form>
    );
};

export default UploadLecture;
